import { Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';
import { Point, LineString, Geometry } from 'ol/geom';
import { Fill, Stroke, Style, Text, Icon, Circle } from 'ol/style';
import { RouteEditModel } from '../models/route-edit-model';
import { Feature, MapBrowserEvent } from 'ol';
import { fromLonLat, toLonLat } from 'ol/proj';
import { Modify, Snap } from 'ol/interaction';
import { Coordinate, toStringHDMS } from 'ol/coordinate';
import { CreateDateLineAndGreatCircleFeatures, generateArcLine, getLegTypeFromDist } from '../utils/map-utils';
import { CoordinatesUtils } from '../utils/coordinates-utils';
import { isStarRouteDeviationEnabled } from '../utils/helpers';
import { STAR_ROUTE_DEVIATION_AMBER } from './types/plan-layer.types';
import { SvgIcons } from '../../local_modules/voyage-svg-icons/src/voyage-svg-icons';
import { getBottomLeft, getTopRight, containsCoordinate, boundingExtent } from 'ol/extent';
import { Map as MGMap } from '@maersk-global/map-layers';
import { VoyageRoutePointFeature } from '../models/voyage-routing-model';
import { StyleFunction } from 'ol/style/Style';
import { FeatureLike } from 'ol/Feature';
import { MapEdgePanningInteraction } from '../../local_modules/voyage-route-editor/src/map-edge-panning-interaction';
import { VesselPlanningModel } from '../models/vessel-planning-model';
import { filterConditionNavigational, getWaypointDisplayNumbers, getWaypointDisplayStartIndex } from '../utils/route-utils';

const _PROJECTION = (<any>window)["appenv"].projection;

export interface RouteEditLayerOptions
{
    lineColor: string;
    waypointFillColor: string;
    textColor: string;
    textOutlineColor: string;
}

interface IndexedWaypoint {
    rawIndex: number;
    waypoint: VoyageRoutePointFeature;
}

// Calculate course between two coordinates in the Mercator projection
function calcCourseMercator(fromPt: Coordinate, toPt: Coordinate) {
    let course = Math.atan2(toPt[0] - fromPt[0], toPt[1] - fromPt[1]) * 180 / Math.PI;
    if (course < 0) course += 360;
    return course;
}

function isHiddenStoppageEndWaypoint(waypoints: VoyageRoutePointFeature[], idx: number): boolean {
    const waypoint = waypoints[idx];
    if (waypoint?.properties?.stoppage_end !== true) {
        return false;
    }

    const prev = idx > 0 ? waypoints[idx - 1] : null;
    return prev?.properties?.is_stoppage_point === true;
}

export class RouteEditLayer extends EventTarget {

    map: MGMap|null = null;

    constructor(options: Partial<RouteEditLayerOptions>) {
        super();
        this.onRouteChanged = this.onRouteChanged.bind(this);
        this.onWaypointSelected = this.onWaypointSelected.bind(this);
        this.getCoordinatesFromVectorSource = this.getCoordinatesFromVectorSource.bind(this);
        this.handleModifyDown = this.handleModifyDown.bind(this);
        this.handleModifyDrag = this.handleModifyDrag.bind(this);
        this.handleModifyUp = this.handleModifyUp.bind(this);
        this._getVesselCharacteristics = this._getVesselCharacteristics.bind(this);

        this._colors = {
            lineColor: "black",
            waypointFillColor: "black",
            textColor: "black",
            textOutlineColor: "white"
        }
        this.setColors(options);
    }

    get layers() {
        return this._layers;
    }

    assignMap(map: MGMap) {
        this.map = map;
        this._mapEdgePanningMode = new MapEdgePanningInteraction(this.map);
    }

    addInteractions() {
        this._modify = new Modify({
            source: this._waypointsVectorSource!,
            // @ts-ignore - OpenLayers typescript definitions are not complete
            handleDownEvent: this.handleModifyDown,
            handleUpEvent: this.handleModifyUp,
            handleDragEvent: this.handleModifyDrag
        });
        this._snap = new Snap({source: this._waypointsVectorSource!});

        this.map!.addInteraction(this._modify);
        this.map!.addInteraction(this._snap);
    }

    handleModifyDown(evt: MapBrowserEvent<UIEvent>)
    {
        // HACK - to handle great circle legs correctly, we need to get to the great circle linestrings before the modify class
        // (so we can remove all points added to make it circular)
        // To correctly detect this, we use the modify's vertex extent. BEWARE: This is somewhat fragile, since it makes some
        // assumptions on the inner workings of the modify class, which could change in the future
        const modifyFeatures = this._modify!.getOverlay().getSource().getFeatures();
        if (modifyFeatures.length > 0)
        {
            const vertexFeature = <Feature<Point>>modifyFeatures[0];
            const coords = vertexFeature.getGeometry()?.getCoordinates();
            const vertexExtent = boundingExtent([coords ? coords : [0, 0]]);

            this._waypointsVectorSource!.forEachFeatureIntersectingExtent(vertexExtent, (feature : Feature<Geometry>) => {
                const geometry = feature.getGeometry();
                if (geometry instanceof LineString)
                {
                    // If the line is a great circle, we need to remove the added great circle points
                    const coords = geometry.getCoordinates();
                    if (coords.length > 2)
                    {
                        // Only keep first and last coordinates + the modifying point *unless* it is one of the end points
                        const newCoords = [coords[0]];
                        if (!((evt.coordinate[0] === coords[0][0] && evt.coordinate[1] === coords[0][1]) ||
                            (evt.coordinate[0] === coords[coords.length-1][0] && evt.coordinate[1] === coords[coords.length-1][1])))
                        {
                            newCoords.push(evt.coordinate);
                        }
                        else
                        {
                            // We are grabbing one of the end points, so make sure we restore the GC lines if the waypoint isn't moved
                            this._gcLinesToRestore.set(<Feature<LineString>>feature, coords);
                        }
                        newCoords.push(coords[coords.length-1]);
                        geometry.setCoordinates(newCoords);
                    }
                }
            });
        }

        return Modify.prototype["handleDownEvent"].call(this._modify, evt);
    }

    handleModifyUp(evt: MapBrowserEvent<UIEvent>) {
        this._mapEdgePanningMode!.disableEdgePanningMode();

        for (const [line, coords] of this._gcLinesToRestore)
        {
            // Great circle line was converted to a straight line to handle dragging, but it didn't happen
            // - restore the circular shape
            line.getGeometry()?.setCoordinates(coords);
        }
        this._gcLinesToRestore.clear();

        if (!this._lastTouchedPoint) {
            const features = this._waypointsVectorSource!.getFeaturesAtCoordinate(evt.coordinate);
            this._lastTouchedPoint = features.find((f: Feature<Geometry>) => f.getGeometry() instanceof Point);
        }

        if (this._lastTouchedPoint && !this._wasDragged) {
            const idx = this._lastTouchedPoint.get('index');
            RouteEditModel!.selectedWaypointIndex = idx !== RouteEditModel!.selectedWaypointIndex ? idx : -1;
        }

        this._lastTouchedPoint = undefined;
        this._wasDragged = false;

        const result = Modify.prototype["handleUpEvent"].call(this._modify, evt);

        this.getCoordinatesFromVectorSource();

        return result;
    }

    handleModifyDrag(evt: MapBrowserEvent<UIEvent>) {
        if (!this._mapEdgePanningMode!.modeIsActive) {
            this._mapEdgePanningMode!.enableEdgePanning();
        }

        Modify.prototype["handleDragEvent"].call(this._modify, evt);

        if (this._lastChangedLines.size === 0) return;

        this._wasDragged = true;
        this._gcLinesToRestore.clear();

        const coord = evt.coordinate;

        let sc: Coordinate[] = [];

        const changedLines: Feature<LineString>[] = Array.from(this._lastChangedLines.values());

        if (this._lastChangedLines.size > 1)
        {
            // Dragging point between lines
            const firstIndex = changedLines.findIndex( line => {
                const coordinates = line.getGeometry()?.getCoordinates();
                return coordinates && coordinates[coordinates.length-1][0] === coord[0] && coordinates[coordinates.length-1][1] === coord[1];
            });
            const firstLineCoords = changedLines[firstIndex].getGeometry()!.getCoordinates();
            const secondLineCoords = changedLines[firstIndex === 0 ? 1 : 0].getGeometry()!.getCoordinates();

            sc = [ firstLineCoords[0], coord,  secondLineCoords[secondLineCoords.length-1]]
        }
        else
        {
            const lineCoords = changedLines[0].getGeometry()!.getCoordinates();
            const idx = lineCoords.findIndex(c => c[0] === coord[0] && c[1] === coord[1]);
            if (idx === -1) return;
            sc = lineCoords.slice(Math.max(idx-1, 0), Math.min(idx+2, lineCoords.length));
        }

        const features = [];
        const gcFeatures = [];

        const mapExtent = this.map!.getView().calculateExtent(this.map!.getSize());

        for (let i=0; i<sc.length-1;i++) {
            const pos1 = toLonLat(sc[i], _PROJECTION);
            const pos2 = toLonLat(sc[i+1], _PROJECTION);

            const course = calcCourseMercator(sc[i], sc[i+1]);
            const dist = CoordinatesUtils.calcDistance(pos1, pos2);

            let distLabel = `${dist.rl.toFixed(1)} Nm [RL]`

            if (dist.gc > 300) {
                distLabel += `\n${dist.gc.toFixed(1)} Nm [GC]`;
                const gcf = CreateDateLineAndGreatCircleFeatures(new Feature({
                    geometry: new LineString([pos1, pos2])
                }));
                gcf.forEach(feature => {
                    feature.getGeometry()?.transform('EPSG:4326', _PROJECTION);
                });
                gcFeatures.push(...gcf);
            }

            let midPoint = [(sc[i][0]+sc[i+1][0])*0.5, (sc[i][1]+sc[i+1][1])*0.5];


            // If one of the points is outside the visible screen we calculate the middle point between
            // the line intersection with the screen and the point that is visible on the screen
            if (!containsCoordinate(mapExtent, sc[i])) {
                const edgePoint = this.calculateIntersectionPoint(sc[i], sc[i+1], getBottomLeft(mapExtent), getTopRight(mapExtent));
                midPoint = [(edgePoint[0]+sc[i+1][0])*0.5, (edgePoint[1]+sc[i+1][1])*0.5];
            }

            if (!containsCoordinate(mapExtent, sc[i+1])) {
                const edgePoint = this.calculateIntersectionPoint(sc[i+1], sc[i], getBottomLeft(mapExtent), getTopRight(mapExtent));
                midPoint = [(sc[i][0]+edgePoint[0])*0.5, (sc[i][1]+edgePoint[1])*0.5];
            }

            const f = new Feature({
                geometry: new Point(midPoint)
            });

            const courseStyle = new Style({
                text: new Text({
                    text: `${course.toFixed(0)}°\n${distLabel}`,
                    font: '20px sans-serif',
                    stroke: new Stroke({
                        color: this._colors.textOutlineColor,
                        width: 4
                    }),
                    fill: new Fill({color: this._colors.textColor})
                })
            });

            f.setStyle(courseStyle);
            features.push(f);
        }

        this._courseVectorSource!.clear();
        this._courseVectorSource!.addFeatures(features);

        this._gcVectorSource!.clear();
        this._gcVectorSource!.addFeatures(gcFeatures);
    }

    // Calculates the intersection point between the line defined by pointA and pointB with the map extent
    // visible on the screen.
    //
    // assumtions: pointA is outside the map extent, pointB is inside the map extent
    //             lowerLeft and upperRight are the corners of the map extent
    //
    // returns the intersection point at the edge of the screen
    calculateIntersectionPoint(pointA: Coordinate, pointB: Coordinate, lowerLeft: Coordinate, upperRight: Coordinate) {
        let t = 0;

        if(pointA[0] === pointB[0]) {
            t =  Math.max((lowerLeft[1] - pointB[1])/(pointA[1] - pointB[1]), (upperRight[1] - pointB[1])/(pointA[1] - pointB[1]));
        } else {
            if(pointA[1] === pointB[1]) {
                t = Math.max((lowerLeft[0] - pointB[0])/(pointA[0] - pointB[0]), (upperRight[0] - pointB[0])/(pointA[0] - pointB[0]));
            } else {
                if(pointA[0] > pointB[0]) {
                    if(pointA[1] > pointB[1]) {
                        t = Math.min((upperRight[0] - pointB[0])/(pointA[0] - pointB[0]), (upperRight[1] - pointB[1])/(pointA[1] - pointB[1]));
                    } else {
                        t = Math.min((upperRight[0] - pointB[0])/(pointA[0] - pointB[0]), (lowerLeft[1] - pointB[1])/(pointA[1] - pointB[1]));
                    }
                } else {
                    if(pointA[1] > pointB[1]) {
                        t = Math.min((lowerLeft[0] - pointB[0])/(pointA[0] - pointB[0]), (upperRight[1] - pointB[1])/(pointA[1] - pointB[1]));
                    } else {
                        t = Math.min((lowerLeft[0] - pointB[0])/(pointA[0] - pointB[0]), (lowerLeft[1] - pointB[1])/(pointA[1] - pointB[1]));
                    }
                }
            }
        }

        return [t * pointA[0] + (1 - t) * pointB[0], t * pointA[1] + (1 - t) * pointB[1]];
    }

    getCoordinatesFromVectorSource() {
        this._courseVectorSource!.clear();
        this._gcVectorSource!.clear();

        if (this._lastChangedLines.size === 0) return;

        let wps = RouteEditModel!.getWaypoints();

        let coordinates: Coordinate[] = [];

        const seqNo = Array.from(this._lastChangedLines)[0].get("seqno");

        let firstLine = true;
        for (const lineString of this._lineStrings.get(seqNo)!)
        {
            const lineCoords = (<LineString>lineString.getGeometry()).getCoordinates();
            if (firstLine)
            {
                coordinates.push(toLonLat(lineCoords[0], _PROJECTION));
                firstLine = false;
            }

            if (this._lastChangedLines.has(lineString))
            {
                // Include all coordinates, except the first one
                for (let i = 1; i < lineCoords.length; i++)
                {
                    coordinates.push(toLonLat(lineCoords[i], _PROJECTION));
                }
            }
            else
            {
                // Only add last coordinate to handle GC lines properly
                coordinates.push(toLonLat(lineCoords[lineCoords.length-1], _PROJECTION));
            }
        }

        coordinates = coordinates.map(coord => {
            // move lon between -180 & 180
            return CoordinatesUtils.normalizeCoordinate(coord);
        });

        // Offset into the total waypoints list (passed waypoints are not part of the coordinates array)
        let idxOffset = 0;
        for (const wp of wps)
        {
            if (!wp.properties.isPassed)
            {
                break;
            }
            idxOffset++;
        }

        for (let index=0; index<coordinates.length; index++)
        {
            if (!this.areCoordsEqual(wps[index+idxOffset].geometry.coordinates, coordinates[index]))
            {
                if (wps.length - idxOffset === coordinates.length)
                {
                    // Waypoint moved
                    wps[index+idxOffset].geometry.coordinates = <[number,number]>coordinates[index];
                }
                else if (wps.length - idxOffset === coordinates.length - 1)
                {
                    // New waypoint added
                    wps.splice(index + idxOffset, 0, {
                        type: "Feature",
                        geometry: { coordinates: <[number,number]>coordinates[index], type: "Point" },
                        properties: {
                            type: "Hard"
                        }
                    });

                    // Set leg_type based on distance
                    if (index < coordinates.length-1)
                    {
                        wps[index+idxOffset].properties.leg_type = getLegTypeFromDist(<[number,number]>coordinates[index], <[number,number]>coordinates[index+1]);
                    }

                    // Leg type for the waypoint before the new waypoint needs to be set as well
                    if (index + idxOffset > 0)
                    {
                        wps[index+idxOffset-1].properties.leg_type = getLegTypeFromDist(wps[index+idxOffset-1].geometry.coordinates, <[number,number]>coordinates[index]);
                    }

                }
                else return;

                // Editing a waypoint invalidates the persisted deviation verdict; clear it so amber
                // does not linger on stale geometry until the next Save & Exit recomputes it.
                wps.forEach(wp => { delete (wp.properties as any).bpr_deviation_exceeds; });

                this._lastChangedLines.clear();
                RouteEditModel!.setNewRoute(wps);
                return;
            }
        }
    }

    areCoordsEqual(coordA: Coordinate, coordB: Coordinate) {
        return coordA !== undefined && coordB !== undefined
                    &&coordA[0].toFixed(5) === coordB[0].toFixed(5)
                    && coordA[1].toFixed(5) === coordB[1].toFixed(5);
    }

    setColors(colors: Partial<RouteEditLayerOptions>) {
        this._colors.lineColor = colors.lineColor || this._colors.lineColor;
        this._colors.waypointFillColor = colors.waypointFillColor || this._colors.waypointFillColor;
        this._colors.textColor = colors.textColor || this._colors.textColor;
        this._colors.textOutlineColor = colors.textOutlineColor || this._colors.textOutlineColor;

        if (this._waypoints) {
            this.plotRoute(this.filteredWaypoints);
        }
    }

    async initialize() {
        // Routes
        this._waypointsVectorSource = new VectorSource({wrapX:false});
        this._waypointsLayer = new VectorLayer({source: this._waypointsVectorSource, visible: true, zIndex: 15});
        this._layers.push(this._waypointsLayer);

        this._passedVectorSource = new VectorSource({wrapX:true});
        this._layers.push(new VectorLayer({source: this._passedVectorSource, visible: true, zIndex: 15}));

        this._courseVectorSource = new VectorSource({wrapX:false});
        this._layers.push(new VectorLayer({source: this._courseVectorSource, visible: true, zIndex: 11}));

        this._gcVectorSource = new VectorSource({wrapX:true});
        this._layers.push(new VectorLayer({source: this._gcVectorSource, visible: true, zIndex: 12}));

        RouteEditModel!.addEventListener('route-changed', <EventListener>this.onRouteChanged);
        RouteEditModel!.addEventListener('waypoint-selected', <EventListener>this.onWaypointSelected);
        VesselPlanningModel!.addEventListener('vessel_characteristics_updated', this._getVesselCharacteristics)
        this._getVesselCharacteristics();
    }

    async _getVesselCharacteristics() {
       const result = await VesselPlanningModel!.getVesselCharacteristics();
       if (!result) return;
       this._ecdisWPStartIndex = result.ecdisWPStartIndex ?? null;
       this._refreshRouteOnMap();
    }

    onRouteChanged(evt: CustomEvent<{waypoints: VoyageRoutePointFeature[]}>) {
        this._waypoints = evt.detail.waypoints;
        this._refreshRouteOnMap();
    }

    onWaypointSelected(evt: CustomEvent<{index: number}>) {
        const index = evt.detail.index;

        // Deselect all but selected
        this._waypointsVectorSource!.getFeatures()
        .filter((f: Feature<Geometry>) => f.getGeometry() instanceof Point)
        .forEach((f: Feature<Geometry>) => f.set('selected', index === f.get('index')));
    }

    plotRoute(waypoints: VoyageRoutePointFeature[]) {
        this._waypoints = waypoints;
        this._lineStrings.clear();
        this._lastChangedLines.clear();

        if (this._waypointsVectorSource)
        {
            this._waypointsVectorSource.clear();
        }

        if (this._passedVectorSource)
        {
            this._passedVectorSource.clear();
        }

        if (waypoints.length === 0)
        {
            return;
        }

        const displayNumbers = getWaypointDisplayNumbers(waypoints, getWaypointDisplayStartIndex(this._ecdisWPStartIndex));
        const passedWPs: VoyageRoutePointFeature[] = [];
        const nonPassedWPs: IndexedWaypoint[] = [];
        for (let i = 0; i < waypoints.length; i++)
        {
            const waypoint = waypoints[i];
            if (waypoint.properties.isPassed)
            {
                if (!isHiddenStoppageEndWaypoint(waypoints, i)) {
                    passedWPs.push(waypoint);
                }
            }
            else
            {
                nonPassedWPs.push({ rawIndex: i, waypoint });
            }
        }
        this._plotPassedWaypoints(passedWPs, nonPassedWPs[0]?.waypoint);
        this._plotModifiableWaypoints(nonPassedWPs, displayNumbers);
    }

    private _layers: VectorLayer<VectorSource<Geometry>>[] = [];
    private _colors: RouteEditLayerOptions;
    private _lastChangedLines: Set<Feature<LineString>> = new Set();
    private _modify: Modify|null = null;
    private _waypointsLayer: VectorLayer<VectorSource<Geometry>>|null = null;
    private _waypointsVectorSource: VectorSource<Geometry>|null = null;
    private _passedVectorSource: VectorSource<Geometry>|null = null;
    private _courseVectorSource: VectorSource<Geometry>|null = null;
    private _gcVectorSource: VectorSource<Geometry>|null = null;
    private _snap: Snap|null = null;
    private _lastTouchedPoint: Feature<Geometry>|undefined = undefined;
    private _wasDragged: boolean = false;
    private _waypoints: VoyageRoutePointFeature[] = [];
    private _lineStrings: Map<number,Feature<LineString>[]> = new Map();
    private _gcLinesToRestore: Map<Feature<LineString>,Coordinate[]> = new Map();
    private _mapEdgePanningMode: MapEdgePanningInteraction|null = null;
    private _ecdisWPStartIndex: number|null = null;


    private get filteredWaypoints(): VoyageRoutePointFeature[] {
        return this._waypoints.filter(filterConditionNavigational);
    }

    private _refreshRouteOnMap() {
        this._clearRoute();
        this.plotRoute(this.filteredWaypoints);

        if (this.filteredWaypoints.length > 0)
        {
            this.addInteractions();
        }
    }

    private _plotModifiableWaypoints(waypoints: IndexedWaypoint[], displayNumbers: (number | null)[])
    {
        if (waypoints.length === 0)
        {
            return;
        }

        const color = this._colors.lineColor;
        const lineStyle = new Style({
            stroke: new Stroke({
                color: `${color}`,
                width: 2,
                lineDash: [.1, 5],
            }),
            zIndex: 2
        });

        // Amber ring around waypoints exceeding the allowed deviation from the top-1 star route.
        // The flag is only set on features when the env toggle is on, so a plain check suffices here.
        const deviationRing = new Style({
            image: new Circle({
                radius: 12,
                stroke: new Stroke({ color: STAR_ROUTE_DEVIATION_AMBER, width: 3 }),
            }),
            zIndex: 19,
        });
        const withDeviationRing = (base: Style, feature: FeatureLike): Style | Style[] =>
            feature.get('bpr_deviation_exceeds') === true ? [base, deviationRing] : base;

        const pointStyle: StyleFunction = (feature: FeatureLike) => {
            return withDeviationRing(new Style({
                text: new Text({
                    text: `${feature.get('text')}`,
                    fill: new Fill({
                        color: '#fff'
                    }),
                    stroke: feature.get('selected') ?
                        new Stroke({color: 'rgba(255, 0, 0, 0.8)', width: 3}) :
                        new Stroke({color: 'rgba(0, 0, 0, 0.6)', width: 3}),
                    scale: 1.8
                }),
                zIndex: 20
            }), feature);
        };

        const pilotSvg = new Image();
        pilotSvg.src = 'data:image/svg+xml,' + encodeURIComponent(SvgIcons.customPilotIcon.strings[0]);

        const pointStylePilot: StyleFunction = (feature: FeatureLike) => {
            return withDeviationRing(new Style({
                image: new Icon({
                    img: pilotSvg,
                    imgSize:[17,17],
                    anchor: [-5,30],
                    anchorYUnits: "pixels",
                    anchorXUnits: "pixels",
                }),
                text: new Text({
                    text: `${feature.get('text')}`,
                    fill: new Fill({
                        color: '#fff'
                    }),
                    stroke: feature.get('selected') ?
                        new Stroke({color: 'rgba(255, 0, 0, 0.8)', width: 3}) :
                        new Stroke({color: 'rgba(0, 0, 0, 0.6)', width: 3}),
                    scale: 1.8
                }),
                zIndex: 20
            }), feature);
        };

        const stoppageSvg = new Image();
        stoppageSvg.src = 'data:image/svg+xml,' + encodeURIComponent(SvgIcons.minusCircle.strings[0]);

        const pointStyleStoppage: StyleFunction = (feature: FeatureLike) => {
            return withDeviationRing(new Style({
                image: new Icon({
                    img: stoppageSvg,
                    imgSize: [19, 19],
                    anchor: [-5, 30],
                    anchorYUnits: "pixels",
                    anchorXUnits: "pixels",
                }),
                text: new Text({
                    text: `${feature.get('text')}`,
                    fill: new Fill({
                        color: '#fff'
                    }),
                    stroke: feature.get('selected') ?
                        new Stroke({color: 'rgba(255, 0, 0, 0.8)', width: 3}) :
                        new Stroke({color: 'rgba(0, 0, 0, 0.6)', width: 3}),
                    scale: 1.8
                }),
                zIndex: 20
            }), feature);
        };

        // for plotting, we need to modify lines crossing the dateline so point 'train' is continuous.
        const continousLon = (lon1: number, lon2: number) => {
            if (Math.abs(lon2 - lon1) > 180) {
                return lon2 + Math.sign(lon1) * 360;
            }
            return lon2;
        }

        const primary: [number,number][] = [];
        let lastLon: number = waypoints[0].waypoint.geometry.coordinates[0];
        for (let i = 0; i < waypoints.length; i++)
        {
            const waypoint = waypoints[i].waypoint;
            let coord = waypoint.geometry.coordinates;
            lastLon = continousLon(lastLon, coord[0]);
            coord = [lastLon, coord[1]];
            primary.push(coord);

        }

        const features: Feature<Geometry>[] = [];

        const shift360 = (coords: [number,number][], n: number) => {
            return coords.map(coord => [coord[0]+n*360, coord[1]] );
        }

        // add linestrings +/- N * 360 (to fix dateline problem while editing)
        [-2,-1,0,1,2].forEach(n => {
            const coords = shift360(primary, n);
            this._lineStrings.set(n, []);

            // Add points
            for (let i = 0; i < coords.length; i++)
            {
                if (i > 0)
                {
                    // Add line
                    let lineCoords: Coordinate[] = [];
                    if (waypoints[i-1].waypoint.properties.leg_type === "GreatCircle")
                    {
                        // If the leg is a great circle leg, we need to inject additional points along the line
                        // Note: The coordinates from generateArcLine are normalized, so we need to adjust them
                        const arc = generateArcLine(waypoints[i-1].waypoint.geometry.coordinates, waypoints[i].waypoint.geometry.coordinates);
                        let lastLon: number = primary[i-1][0];
                        for (const arcCoords of arc)
                        {
                            for (const arcCoord of arcCoords)
                            {
                                lastLon = continousLon(lastLon, arcCoord[0]);
                                lineCoords.push([lastLon+n*360, arcCoord[1]]);
                            }
                        }
                    }
                    else
                    {
                        lineCoords = [coords[i-1], coords[i]];
                    }

                    const feature = new Feature(new LineString(lineCoords));
                    feature.getGeometry()?.transform('EPSG:4326', _PROJECTION);
                    feature.setStyle(lineStyle);
                    feature.set('seqno', n);
                    feature.on('change', () => { this._lastChangedLines.add(feature); });
                    features.push(feature);
                    this._lineStrings.get(n)!.push(feature);
                }

                const rawIndex = waypoints[i].rawIndex;
                const displayNum = displayNumbers[rawIndex];
                if (displayNum === null) {
                    continue;
                }

                const f = new Feature({
                    geometry: new Point(fromLonLat(coords[i], _PROJECTION)),
                    id: `waypoint_${rawIndex}`
                });

                if (waypoints[i].waypoint.properties.pilotPoint) {
                    f.setStyle(pointStylePilot);
                } else if (waypoints[i].waypoint.properties.is_stoppage_point === true) {
                    f.setStyle(pointStyleStoppage);
                } else {
                    f.setStyle(pointStyle);
                }

                if (isStarRouteDeviationEnabled() && (waypoints[i].waypoint.properties as any).bpr_deviation_exceeds === true) {
                    f.set('bpr_deviation_exceeds', true);
                }

                f.set('textcoords', `${toStringHDMS(coords[i])}`);
                f.on('change', () => { this._lastTouchedPoint = f; });
                f.set('index', rawIndex);
                f.set('text', `${displayNum}`);
                f.set('selected', rawIndex === RouteEditModel!.selectedWaypointIndex);
                features.push(f);
            }
        });

        this._waypointsVectorSource!.addFeatures(features);
    }

    private _clearRoute() {
        try {
            // The interactions are permanently attached to the source, so we need to remove the source entirely to get rid of them
            this._waypointsVectorSource = new VectorSource({wrapX:false});
            this._waypointsLayer!.setSource(this._waypointsVectorSource);
            this.map!.removeInteraction(this._modify!);
            this.map!.removeInteraction(this._snap!);
            this._modify = null;
            this._snap = null;
            this._passedVectorSource!.clear();
        } catch (error) {
            console.warn(error);
        }
    }

    private _plotPassedWaypoints(waypoints: VoyageRoutePointFeature[], firstModifiableWP: VoyageRoutePointFeature): void
    {
        if (waypoints.length === 0)
        {
            return;
        }

        const lineStyle = new Style({
            stroke: new Stroke({
                color: this._colors.lineColor, width: 1
            })
        });

        const wpStyle = new Style({
            image: new Circle({
                radius: 4,
                fill: new Fill({color: this._colors.lineColor.trim()}),
                stroke: new Stroke({
                  color: this._colors.lineColor, width: 1
                })
            })
        });

        const features: Feature<Geometry>[] = [];
        for (let i = 0; i < waypoints.length; i++)
        {
            const waypoint = waypoints[i];
            const nextWaypoint = i < waypoints.length - 1 ? waypoints[i+1] : firstModifiableWP;

            const point = new Feature(new Point(waypoint.geometry.coordinates));
            point.getGeometry()?.transform('EPSG:4326', _PROJECTION);
            point.setStyle(wpStyle);
            features.push(point);

            // Add line to next waypoint
            const line = new Feature(new LineString([waypoint.geometry.coordinates, nextWaypoint.geometry.coordinates]));
            const handledLine = CreateDateLineAndGreatCircleFeatures(line);
            handledLine.forEach(line => {
                line.getGeometry()?.transform('EPSG:4326', _PROJECTION);
                line.setStyle(lineStyle);
            })
            
            features.push(...handledLine);

        }
        this._passedVectorSource!.addFeatures(features);
    }
};
